// unit test for this file
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import RoundedContainer from '../../../common/components/RoundedContainer';
import {
  PhoneNumberRequired,
  validatePhoneNumber,
  validatePhoneNumberOnlyDigits,
} from '../../../common/utils/validationUtils';
import { ContactRoles } from '../../../types/ContactType';
import { ResponseError } from '../../../types/ResponseError';

interface ContactFormProps {
  data?: ContactFormValues;
  roles?: ContactRoles[];
  onSubmit: (data: ContactFormValues, successCallback?: () => void) => Promise<void>;
}

export interface ContactFormValues {
  id?: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  roleId: number;
}

const defaultValues: ContactFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  roleId: 1,
};

const StyleTextField = styled(TextField)(({ theme }) => ({
  [`&.MuiFormControl-root`]: {
    width: '100%',

    [theme.breakpoints.up('md')]: {
      width: '49%',
    },
  },
}));

export default function ContactForm({ data, roles, onSubmit }: ContactFormProps) {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const [pageErrors, setPageErrors] = useState<string[]>([]);

  const onFormSubmit = async (data: any) => {
    try {
      await onSubmit(data, () => {
        if (!data.id) {
          reset();
        }
        setPageErrors([]);
      });
    } catch (e) {
      const {
        data: { error },
      } = e as ResponseError;
      setPageErrors(error);
    }
  };

  const validationSchema = yup.object({
    firstName: yup.string().required(t('account.contacts.form.error.blankFirstName')),
    lastName: yup.string().required(t('account.contacts.form.error.blankLastName')),
    email: yup
      .string()
      .email(t('account.contacts.form.error.invalidEmail'))
      .required(t('account.contacts.form.error.blankEmail')),
    phone: yup
      .string()
      .trim()
      .required(t('account.contacts.form.error.blankPhone'))
      .test('is-valid-phone', t('account.contacts.form.error.invalidPhone'), validatePhoneNumberOnlyDigits)
      .test(
        'too-short-phone',
        t('account.contacts.form.error.phoneNumberTooShort', {
          minLength: PhoneNumberRequired.MinLengthPhoneNumber,
        }),
        validatePhoneNumber
      ),
    roleId: yup
      .number()
      .typeError(t('account.contacts.form.error.blankRole'))
      .required(t('account.contacts.form.error.blankRole')),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  useEffect(() => {
    if (data) {
      reset(data);
    }
  }, [data, reset]);

  return (
    <Box component="form" noValidate onSubmit={handleSubmit(onFormSubmit)} sx={{ px: 0, pt: 0 }}>
      <RoundedContainer
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          px: 0,
          py: 0,
          pb: 2,
        }}
      >
        <Controller
          name="firstName"
          control={control}
          render={({ field }) => (
            <StyleTextField
              {...field}
              label={t('account.contacts.form.firstName')}
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              variant="outlined"
              margin="normal"
              fullWidth
            />
          )}
        />
        <Controller
          name="lastName"
          control={control}
          render={({ field }) => (
            <StyleTextField
              {...field}
              label={t('account.contacts.form.lastName')}
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              variant="outlined"
              margin="normal"
              fullWidth
            />
          )}
        />
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <StyleTextField
              {...field}
              label={t('account.contacts.form.email')}
              error={!!errors.email}
              helperText={errors.email?.message}
              variant="outlined"
              margin="normal"
              fullWidth
            />
          )}
        />
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <StyleTextField
              {...field}
              label={t('account.contacts.form.phone')}
              error={!!errors.phone}
              helperText={errors.phone?.message}
              variant="outlined"
              margin="normal"
              fullWidth
            />
          )}
        />
        <Controller
          name="roleId"
          control={control}
          render={({ field }) => (
            <StyleTextField
              {...field}
              label={t('account.contacts.form.role')}
              error={!!errors.roleId}
              helperText={errors.roleId?.message}
              variant="outlined"
              margin="normal"
              select
              fullWidth
            >
              {roles?.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.prettyName}
                </MenuItem>
              ))}
            </StyleTextField>
          )}
        />
      </RoundedContainer>

      <RoundedContainer sx={{ paddingTop: 2, marginBottom: 2 }}>
        <Box
          sx={{
            width: '100%',
            float: 'right',
            pb: 3,
          }}
        >
          {!matches && <Divider sx={{ py: 1 }} />}
          {pageErrors?.length > 0 &&
            pageErrors.map((error) => (
              <Alert key={error} severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            ))}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              width: matches ? '100%' : '24%',
              my: 2,
              py: 2,
              float: 'right',
            }}
          >
            {data?.id ? t('account.contacts.editContact') : t('account.contacts.addContact')}
          </Button>
        </Box>
      </RoundedContainer>
    </Box>
  );
}
